import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import PhoneInput, {
  parsePhoneNumber,
  isPossiblePhoneNumber,
} from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { Segment, Form, Input } from "semantic-ui-react";
import ICAddressForm from "./ICAddressForm";
import NonICAddressForm from "./NonICAddressForm";
import "react-phone-number-input/style.css";

const ContactInfoForm = ({
  currentFieldValue,
  updateField,
  invalidFieldValue,
  canValidateField,
  content = null,
  disabled = false,
  isInternationalIndependentContractor = false,
}) => {
  const [fullPhoneNumber, setFullPhoneNumber] = useState(null);
  const [isPhoneError, setIsPhoneError] = useState(null);

  // Initialize fullPhoneNumber with existing backend data
  useEffect(() => {
    const phone = currentFieldValue("phone");
    const phoneCountryCode = currentFieldValue("phone_country_code");
    if (phone && phoneCountryCode && !fullPhoneNumber) {
      // Combine country code and phone number for the PhoneInput component
      const combinedPhone = `${phoneCountryCode}${phone}`;
      setFullPhoneNumber(combinedPhone);
    }
  }, [currentFieldValue, fullPhoneNumber]);

  const validatePhone = () => {
    if (!fullPhoneNumber) return false;

    const isValid = isPossiblePhoneNumber(fullPhoneNumber);

    if (!isValid) return setIsPhoneError(true);

    return setIsPhoneError(false);
  };

  const updateFormPhoneState = async (countryCode, basePhone) => {
    await updateField("phone_country_code", `+${countryCode}`);
    await updateField("phone", basePhone);
  };

  const parsePhone = (value) => {
    const parsedNumber = parsePhoneNumber(value);
    const countryCode = parsedNumber?.countryCallingCode;
    const nationalNumber = parsedNumber?.nationalNumber;

    return { countryCode, nationalNumber };
  };

  const handlePhoneChange = async (value) => {
    setFullPhoneNumber(value);

    const isValid = isPossiblePhoneNumber(value);
    if (isValid) setIsPhoneError(!isValid);

    const { countryCode, nationalNumber } = parsePhone(value);
    // nationalNumber cannot be validated in isolation, so only store it if it's valid
    const phoneBase = isValid ? nationalNumber : null;
    await updateFormPhoneState(countryCode, phoneBase);
  };

  const addressFormProps = {
    currentFieldValue,
    updateField,
    invalidFieldValue,
    canValidateField,
    content,
    disabled,
  };

  return (
    <Segment>
      {typeof content === "function" ? content() : null}
      <Form>
        {isInternationalIndependentContractor ? (
          <ICAddressForm {...addressFormProps} />
        ) : (
          <NonICAddressForm {...addressFormProps} />
        )}

        {/* Forking of logic here depending on whether the phone number
        returned from endpoint is masked - we have to tweak the Form.Field component to make
        sure it displays properly. */}
        {(() => {
          const phoneValue = currentFieldValue("phone");
          const isMasked = /\*/.test(phoneValue);
          return isMasked;
        })() ? (
          <Form.Field disabled>
            <label htmlFor="phone">Phone number</label>
            <Input name="phone" value={currentFieldValue("phone")} />
          </Form.Field>
        ) : (
          <Form.Field
            error={isPhoneError}
            onBlur={validatePhone}
            disabled={disabled}
          >
            <label htmlFor="new-user-phone-number">Phone number</label>
            <PhoneInput
              autoComplete="tel"
              id="new-user-phone-number"
              type="tel"
              value={fullPhoneNumber}
              flags={flags}
              onChange={(value) => handlePhoneChange(String(value))}
              name="new-user-phone-number"
              placeholder="Phone number"
            />
          </Form.Field>
        )}
      </Form>
    </Segment>
  );
};

ContactInfoForm.propTypes = {
  currentFieldValue: PropTypes.func.isRequired,
  updateField: PropTypes.func.isRequired,
  invalidFieldValue: PropTypes.func.isRequired,
  canValidateField: PropTypes.func.isRequired,
  content: PropTypes.func,
  disabled: PropTypes.bool,
  isInternationalIndependentContractor: PropTypes.bool,
};

export default ContactInfoForm;
